import {
  MutationFunction,
  useMutation,
  UseMutationOptions,
  UseMutationResult,
} from '@tanstack/react-query';
import { APISchema, castAPIData } from 'shared/api/APISchema';
import { APIResponse } from 'shared/api/CarrierAPIClient';

type APIMutationFunction<TInput, TData = unknown> = MutationFunction<
  APIResponse<TData>,
  TInput
>;

export type APIMutationOptions<
  TInput = void,
  TData = unknown,
  TContext = unknown,
> = Omit<
  UseMutationOptions<APIResponse<TData>, Error, TInput, TContext>,
  'mutationKey'
>;

export interface UseAPIMutationOptions<
  TInput = void,
  TData = unknown,
  TContext = unknown,
> extends APIMutationOptions<TInput, TData, TContext> {
  schema?: APISchema<TData>;
}

export type APIMutationResult<
  TInput = void,
  TData = unknown,
  TContext = unknown,
> = UseMutationResult<APIResponse<TData>, Error, TInput, TContext>;

export function useAPIMutation<TInput, TData = unknown, TContext = unknown>(
  mutationFn: APIMutationFunction<TInput, TData>,
  { schema, ...options }: UseAPIMutationOptions<TInput, TData, TContext> = {},
): APIMutationResult<TInput, TData, TContext> {
  return useMutation({
    mutationFn: !schema
      ? mutationFn
      : (input, context) =>
          mutationFn(input, context).then((response) => ({
            ...response,
            data: castAPIData(schema, response.data),
          })),
    ...options,
  });
}
